import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/Reveal";
import { ASSETS } from "@/data/content";

const About = () => {
  const { t, dir } = useLang();

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-[#F5F2EB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-20">
          <Reveal>
            <Kicker>{t.about.kicker}</Kicker>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              style={{ lineHeight: dir === "rtl" ? 1.25 : 1.1 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] mt-5"
            >
              {t.about.title}
            </h2>
          </Reveal>
        </div>

        {/* Numbered manifesto chapters + spotlight image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7 space-y-12">
            {t.about.chapters.map((c, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="flex gap-6 md:gap-10 border-t border-[#E5DFD3] pt-10">
                  <span className="font-display text-[#6A1E27] text-2xl md:text-3xl shrink-0 w-14">
                    0{i + 1}
                  </span>
                  <div>
                    <div className="flex items-baseline gap-4 mb-3 flex-wrap">
                      <span className="font-display text-3xl md:text-4xl text-[#1A1A1A]">
                        {c.year}
                      </span>
                      <span className="text-sm uppercase tracking-[0.2em] text-[#C4A27A]">
                        {c.title}
                      </span>
                    </div>
                    <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed max-w-xl">
                      {c.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal delay={0.2}>
              <div className="relative overflow-hidden">
                <motion.img
                  src={ASSETS.factoryImg}
                  alt="Central Factory"
                  className="w-full h-[420px] md:h-[560px] object-cover"
                  initial={{ scale: 1.2 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0B0B0B]/80 to-transparent p-6">
                  <p className="text-[#EBD9AC] text-sm uppercase tracking-[0.2em]">5,600 m²</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-24">
          <Reveal>
            <div className="bg-[#6A1E27] text-[#F5F2EB] p-10 md:p-14 h-full">
              <h3 className="font-display text-2xl md:text-3xl mb-4">{t.about.visionTitle}</h3>
              <p className="text-base md:text-lg leading-relaxed text-[#F0E8DC]">{t.about.vision}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white border border-[#E5DFD3] p-10 md:p-14 h-full">
              <h3 className="font-display text-2xl md:text-3xl mb-4 text-[#1A1A1A]">
                {t.about.missionTitle}
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]">{t.about.mission}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
